import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select } from '~/components/Form';
import { t } from '~/helpers';
import { storeData, updateData } from '~/store/modules/excecaoMeta/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.excecaoMeta);

  const initials = useMemo(
    () => (create ? { statusexcecaometa: 'L', ...data } : { ...find, ...data }),
    [create, data, find]
  );

  const status = useMemo(
    () => [
      { value: 'L', label: t('s.geral.opcoes.liberado') },
      { value: 'C', label: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  async function handleSubmit(param) {
    try {
      const normalizedParam = {
        ...param,
        numeroexcecaometa: param.numeroexcecaometa?.replace(/\s/g, '') || '',
        descricaoexcecaometa: param.descricaoexcecaometa?.trim() || '',
      };

      const schema = Yup.object().shape({
        numeroexcecaometa: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .matches(/^[0-9.]+$/, t('s.geral.validacao.camponumerico')),

        descricaoexcecaometa: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(400, t('s.geral.validacao.tamanhomaximo').replace('%s', 400)),

        statusexcecaometa: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .oneOf(['L', 'C'], t('s.geral.validacao.campoinvalido')),
      });

      await schema.validate(normalizedParam, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...normalizedParam,
        idcliente: client,
      };

      if (create) {
        dispatch(storeData(objSend, params));
      } else {
        dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials, reference]);

  return loading ? (
    <div style={{ height: '14rem', width: '50rem' }}>
      <Spinner loading />
    </div>
  ) : (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <Input
        name="numeroexcecaometa"
        label={t('s.cadastro.pitstop.numeroexcecaometa')}
        variant="outlined"
        fullWidth
        size="small"
        inputProps={{ maxLength: 10 }}
        InputLabelProps={{ shrink: true }}
      />

      <Input
        name="descricaoexcecaometa"
        label={t('s.cadastro.pitstop.descricaoexcecaometa')}
        variant="outlined"
        fullWidth
        size="small"
        inputProps={{ maxLength: 400 }}
        InputLabelProps={{ shrink: true }}
      />

      <Select
        name="statusexcecaometa"
        label={t('s.cadastro.pitstop.statusexcecaometa')}
        options={status}
      />
    </Form>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
